<template>
    <ui-overlay-load v-if="overlay && (loading || reload)" />

    <div v-else-if="loading || reload" class="load-indicator">
        <ui-load-indicator bg-color="#C9C9E8" color="#0091FF" :h="50" :stroke="8" />
    </div>
    <div
        v-else
        id="table-content"
        :class="[sizeControl, { 'table-header-fixed': isFixedList }, {'control-fixed': controlFixed}, {'select-column-menu': selectColumnMenu}, {'zebra': zebra}, {'line-cols': borders}]"
        class="ui-table scroll-color"
    >
        <section v-if="totalPages" class="ui-table-content scroll-color" >
            <table v-if="totalPages">
                <thead ref="thead">
                    <tr>
                        <th v-if="showCheckboxTable" scope="col">
                            <uiCheckbox
                                :checked.sync="allSelected"
                                @input="toggleSelectAll"
                                design="sm primary"
                            />
                        </th>
                        <th
                            v-for="(column, index) in columns"
                            :key="index"
                            :width="column.size || 'auto'"
                            :class="column.class"
                            :data-index="snakeCase(column.title)"
                            scope="col"
                        >
                            <div class="th-infos">
                                <div>{{ column.title }}</div>
                                <div v-if="column.sortable">
                                    <i :class="checkSortClass(column)"/>
                                </div>
                            </div>
                            <div v-if="column.sortable && column.data" class='click-sort' @click="sort(column.data)" />

                            <!-- Verifica se é a ultima coluna e tem a opção de mostrar o menu ativada -->
                            <template v-if="selectColumnMenu && index == (columns.length - 1)">
                                <ui-menu-card icon="fal fa-cog">
                                    <button
                                        v-for="(col, i) in columnsComputed"
                                        :key="i"
                                        :class="setActiveClass(snakeCase(col.title))"
                                        type="button"
                                        @click="setColumn(snakeCase(col.title))">
                                        {{ col.title }}
                                    </button>
                                </ui-menu-card>
                            </template>
                        </th>
                    </tr>
                </thead>
                <tbody ref="tbody" class="body">
                    <component
                        :is="component"
                        v-for="(item, index) in datatable.data"
                        :key="`${index}-item-${item.id}`"
                        :total="datatable.total"
                        :data="item"
                        :index="index"
                        :showCheckboxLine="showCheckboxTable"
                        :is-permission-to-change-state="isPermissionToChangeState" 
                        @hook:mounted="prepareTable(index)"
                        @hook:updated="prepareTable(index)"
                        @update-line="updateLineHandler"
                    />
                </tbody>
            </table>
        </section>

        <ui-page-message
            v-if="!totalPages && !loading"
            :icon="notFoundIcon"
            icon-size="60"
            :bg="true"
            :text="notFoundText" />

        <div v-if="pagination" :class="{ 'fixed-botton': paginationFixedBotton }">
            <section v-if="totalPages" class="ui-pagination mt-30">
                <div v-if="msg" class="msg">
                    {{ msg }}
                </div>
                <div class="pagination-end">
                    <div v-if="totalPages > 1" class="text">
                        Página {{ datatable.params.page }} de {{ totalPages }}
                    </div>

                    <div v-if="totalPages > 1" class="control">
                        <button
                            :class="sizeControl"
                            :disabled="!(totalPages > 1)"
                            type="button"
                            @click="previousPageHandler">
                            <i class="fal fa-angle-left" />
                        </button>

                        <div v-if="paginateList" class="control-list">
                            <button
                                v-for="(page, index) in paginationList"
                                :key="index"
                                :class="{ sizeControl, 'active': isActivePage(page.value) }"
                                type="button"
                                @click="listPageHandler(page.value)">
                                {{ page.label }}
                            </button>
                        </div>

                        <button
                            :class="sizeControl"
                            :disabled="!(totalPages > 1)"
                            type="button"
                            @click="nextPageHandler">
                            <i class="fal fa-angle-right" />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import { uiLoadIndicator, uiOverlayLoad, uiMenuCard } from "@/components/ui"
    import { uiCheckbox } from "@/components/ui/form"
    import uiPageMessage from "@/components/ui/page-message"
    import store from "@/store"
    import axios from "axios"

    import { helpersMixin, tablesMixin } from '@/mixins'

    export default {
        name: 'ui-data-table',

        components: {
            uiLoadIndicator,
            uiOverlayLoad,
            uiPageMessage,
            uiMenuCard,
            uiCheckbox
        },

        mixins: [ helpersMixin, tablesMixin ],

        props: {
            trComponent: {
                type: String,
                required: false,
                default: ''
            },
            columns: {
                type: Array,
                required: true,
            },
            sortBy: {
                type: Object,
                required: false,
                default() {
                    return {
                        value: "value",
                        dir: "asc",
                    }
                },
            },
            sizeControl: {
                type: String,
                default: "sm",
            },
            tableClass: {
                type: String,
                default: "ui-table-content",
            },
            paginationFixedBotton: {
                type: Boolean,
                default: false,
            },
            isFixedList: {
                type: Boolean,
                required: false,
                default: true,
            },
            msg: {
                type: String,
                default: '',
                required: false,
            },
            searchEvent: {
                type: String,
                required: false,
                default: "datatable-search",
            },
            service: {
                type: String,
                required: true,
                default: ''
            },
            name: {
                type: String,
                required: true,
                default: ''
            },
            notFoundIcon: {
                type: String,
                required: true,
                default: ''
            },
            notFoundText: {
                type: String,
                required: true,
                default: ''
            },
            perPage: {
                type: Number,
                required: false,
                default: 25,
            },
            /**
             * Define o layout do componente.
             * Torna a ultima TD/coluna da tabela fixa afim de
             * sempre mostrar o menu de acção da linha da tabela
             */
            controlFixed: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * Define o comportamento do menu de acção da tabela.
             * Neste menu é possível selecionar quais colunas mostrar ou ocultar
             */
            selectColumnMenu: {
                type: Boolean,
                required: false,
                default: true,
            },
            filters: {
                type: Array,
                required: false,
                default() {
                    return []
                }
            },
            zebra: {
                type: Boolean,
                required: false,
                default: true,
            },
            borders: {
                type: Boolean,
                required: false,
                default: false,
            },
            paginateList: {
                type: Boolean,
                required: false,
                default: true
            },
            paginateListSize: {
                type: [String, Number],
                required: false,
                default: 5
            },
            pagination: {
                type: Boolean,
                default: true
            },
            /**
             * Define se o loading da lista será do tipo Overlay ou Indicator
             */
            overlay: {
                type: Boolean,
                requried: false,
                default: true
            },
            /**
             * Variável que ativa ou desativa as checkbox para seleções em massa
             */
            showCheckboxTable: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * Variável que verifica se na lista de clientes ele tem permissão para alterar etapa e status
             */
            isPermissionToChangeState: {
                type: Boolean,
                required: false,
                default: false
            }
        },

        data() {
            return {
                component: null,
                api: null,
                datatable: {
                    params: {
                        sortBy: { ...this.sortBy },
                        search: "",
                        page: 1,
                        perPage: this.perPage,
                        filter: [],
                    },
                    data: [],
                    total: 0,

                },
                loading: true,
                reload: false,
                timeout: null,
                paginateListIndex: 0,
                getFilters: [],
                tags: {
                    important: false
                },
                allSelected: false,
                isRequesting: false
            }
        },

        computed: {
            loadService() {
                return () => import("@/services/axios/modules/" + this.service + ".js")
            },
            totalPages: {
                get() {
                    let quotient = Math.floor(this.datatable.total / this.datatable.params.perPage)
                    let remainder = this.datatable.total % this.datatable.params.perPage
                    return remainder === 0 ? quotient : quotient + 1
                },
            },
            /**
             * Computed property que retorna uma lista de páginas disponíveis para navegação,
             * bem como opções para avançar ou regredir a lista
             */
            paginationList() {
                const list = []
                const currentPage = this.datatable.params.page
                const totalPages = this.totalPages
                const maxPages = 5 // Número máximo de páginas exibidas na tela

                // Cálculo do índice do grupo atual de páginas
                const currentIndex = Math.ceil(currentPage / maxPages)

                // Cálculo do início e fim do grupo atual de páginas
                let startPage = (currentIndex - 1) * maxPages + 1
                let endPage = Math.min(currentIndex * maxPages, totalPages)

                // Adicionar botão "..." à esquerda se necessário
                if (startPage > 1) {
                    list.push({ label: '...', value: startPage - maxPages })
                }

                // Adicionar as páginas à lista
                for (let i = startPage; i <= endPage; i++) {
                    list.push({ label: i.toString(), value: i })
                }

                // Adicionar botão "..." à direita se necessário
                if (endPage < totalPages) {
                    list.push({ label: '...', value: endPage + 1 })
                }

                return list
            },
        },

        watch: {
            /**
             * Caso ocorra uma alteração no service, carrega-se o novo service e seta-o para
             * ser utilizado na busca
             */
            service() {
                this.loadService().then(async response => {
                    this.api = response.default.build()
                    await this.getData()
                })
            },
            /**
             * Caso ocorra uma alteração no component de linha, carrega-se o novo component
             * e seta-o para ser utilizado na lista
             */
            trComponent() {
                this.loadComponent().then(() => {
                    this.component = () => this.loadComponent()
                })
            },
            /**
             * Caso ocorra uma alteração nas colunas, carrega-se os dados das novas colunas
             */
            columns() {
                this.visibleColumns = this.columns.map((x) => {
                    return this.snakeCase(x.title)
                })
            },
            'datatable.data': {
              handler(newValue) {
                let selectableItems = newValue.filter(item => !item.disable);
                let count = newValue.filter(item => {
                    if(item.checked) return item
                });

                if (count.length < selectableItems.length) {
                    this.allSelected = false
                } else if (count.length === selectableItems.length) {
                    this.allSelected = true
                }
                this.$emit('checked-itens', count)
              },
              deep: true,
            },
        },

        beforeMount() {
            this.loadComponent().then(() => {
                this.component = () => this.loadComponent()
            })
        },

        created() {
            this.getColumns()
        },

        beforeDestroy() {
            this.$root.$off(this.searchEvent)
            this.$root.$off("datatable-reload-" + this.name)
        },

        mounted() {
            this.loadService().then(async response => {
                this.api = response.default.build()
                await this.getData()
            })

            this.$root.$on(this.searchEvent, async e => {
                this.datatable.params.search = e
                this.datatable.params.page = 1
                this.searchEventHandler()
            })

            this.$root.$on("datatable-reload-" + this.name, () => {
                this.getData()
            })
            
            /**
             *  Função comentada para posteriormente implementar atualização reativa na tabela, sem necessidade de fazer nova requisição.
             */
            // this.$root.$on("datatable-update-" + this.name, (data) => {
            //     this.datatable.data = data;
            // })
            // this.$root.$on("datatable-update-" + this.name, (updatedUsers) => {
            //     this.datatable.data = this.datatable.data.map(user => {
            //         const updatedUser = updatedUsers.find(u => u.id === user.id);
            //         return updatedUser ? { ...user, ...updatedUser } : user;
            //     });
            // });
        },

        methods: {
            /**
             * Busca os dados a serem listados, de acordo com o service definido e os parâmetros
             * de filtragem
             */
            async getData() {
                if(this.isRequesting) return;
                this.isRequesting = true
                this.reload = !this.loading ? true : false
                let params = { ...this.datatable.params }
                params.filter = this.getFilters.length > 0  ? [...this.getFilters] : [...this.filters]
                this.datatable.data = []
                this.loading = true
                await this.api.dataTable(params)
                    .then((response) => {
                        /**
                         * passa os dados para salvar os dados do filtro no localStorage
                         */
                        this.$root.$emit('set-filter-selected-active', params.filter)
                        this.datatable.data = response.data.data
                        if (this.showCheckboxTable) {
                            this.datatable.data = response.data.data.map(item => ({
                                ...item,
                                checked: false, // Adiciona a propriedade checked a cada item
                                disable: false
                            }));
                        }
                        let total = response.data.recordsFiltered ||  response.data.total
                        this.datatable.total = this.datatable.data.length > 0 ? total : 0
                        this.$store.dispatch('dataTable/setTotal', this.datatable.total)
                        this.reload = false
                    })
                    .catch((error) => {
                        if (!axios.isCancel(error)) {
                            console.log(error)
                        }
                    })
                    .finally(() => {
                        this.loading = false
                        this.isRequesting = false
                        this.getFilters = []
                    })
            },
            /**
             * Retorna a classe de ordenação correta para a coluna, de acordo com os parâmetros
             * setados para o DataTable
             */
            checkSortClass(column) {
                let classValue = 'fas fa-sort'

                if (column.data === this.datatable.params.sortBy.value
                    && this.datatable.params.sortBy.dir === 'asc') {
                    classValue = 'fas fa-sort-up'

                } else if (column.data === this.datatable.params.sortBy.value
                    && this.datatable.params.sortBy.dir === 'desc') {
                    classValue = 'fas fa-sort-down'
                }
                return classValue
            },
            searchEventHandler: async function () {
                if (this.isRequesting) return
                this.$root.$emit('ui-search-box-loading-true');
                if (store.getters.datatableCancel) await store.getters.datatableCancel()
                await this.getData()
                this.$root.$emit('ui-search-box-loading-false'); 
            },
            nextPageHandler() {
                let page = this.datatable.params.page + 1
                this.setPage(page)
            },
            previousPageHandler() {
                let page = this.datatable.params.page - 1
                this.setPage(page)
            },
            updateLineHandler(event) {
                this.$set(this.datatable.data, event.index, event.value)
            },
            /**
             * Event handler que trata o evento de quando uma página é selecionada diretamente
             * pela lista de páginas
             */
            listPageHandler(value) {
                const currentPage = this.datatable.params.page

                switch (value) {
                    case 'right':
                        if (currentPage < this.totalPages) {
                            this.paginateListIndex++
                            this.setPage(currentPage + 1)
                        }
                        break

                    case 'left':
                        if (currentPage > 1) {
                            this.paginateListIndex--
                            this.setPage(currentPage - 1)
                        }
                        break

                    default:
                        this.setPage(value)
                        break
                }
            },
            /**
             * Seta a página ativa na listagem
             */
            setPage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.$set(this.datatable.params, 'page', page)
                    this.getData()
                }
            },
            /**
             * Realiza a ordenação da listagem de acordo com uma coluna configurada
             */
            sort(event) {
                if (this.datatable.params.sortBy &&
                    this.datatable.params.sortBy.value === event) {
                    switch (this.datatable.params.sortBy.dir) {
                        case 'asc':
                            this.$set(this.datatable.params.sortBy, 'dir', 'desc')
                            break

                        default:
                            if (this.datatable.params.sortBy.value === this.sortBy.value) {
                                this.$set(this.datatable.params.sortBy, 'dir', 'asc')

                            } else {
                                this.$set(this.datatable.params, 'sortBy', { ...this.sortBy })
                            }
                            break
                    }

                } else {
                    this.$set(this.datatable.params, 'sortBy', { value: event, dir: 'asc' })
                }
                this.getData()
            },
            /**
             * Verifica se a página é aquela selecionada para paginação
             */
            isActivePage(page) {
                return this.datatable.params.page === page
            },
            toggleSelectAll() {
                // console.log(this.datatable.data)
                if (this.allSelected) {
                    this.datatable.data = this.datatable.data.map(item => ({
                        ...item,
                        checked: true 
                    }));
                } else {
                    this.datatable.data = this.datatable.data.map(item => ({
                        ...item,
                        checked: false 
                    }));
                }
            },
        },
    }
</script>

<style lang="stylus" scoped>
    @import 'styles.styl'
</style>